// The TimeComponent class represents a draggable clock component with both analog and digital displays.
export class TimeComponent extends HTMLElement {
    // Indicates whether the clock is visible.
    private _isVisible: boolean = true;

    // Stores the interval ID for updating the clock.
    private _intervalId: number | null = null;

    // The main clock element.
    private _clockElement: HTMLDivElement;

    // Determines whether the clock uses 24-hour format.
    private _is24HourFormat: boolean = false;

    // Stores the offset for dragging the clock.
    private _dragOffset = { x: 0, y: 0 };

    // Indicates whether the clock is currently being dragged.
    private _isDragging = false;

    // Constructor initializes the clock element.
    public constructor() {
        super();
        this._clockElement = document.createElement("div");
    }

    // Lifecycle method called when the component is added to the DOM.
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });
        this.shadowRoot?.appendChild(this._clockElement);
        this.render();
        this.makeDraggable();
    }

    // Lifecycle method called when the component is removed from the DOM.
    public disconnectedCallback(): void {
        this.stopClock();
    }

    // Renders the clock's HTML and styles.
    private render(): void {
        if (!this.shadowRoot) return;

        this._clockElement.innerHTML = `
            <style>
                :host {
                    position: fixed;
                    top: 20px;
                    right: 0px;
                    z-index: 1000;
                    cursor: move;
                    font-family: "Onesize";
                }

                .clock-container {
                    display: ${this._isVisible ? "block" : "none"};
                    width: 150px;
                    height: 150px;
                    border-radius: 50%;
                    background: radial-gradient(circle at center, #f8f8f8 0%, #e0e0e0 100%);
                    box-shadow: 
                        0 0 25px rgba(0, 0, 0, 0.6),
                        inset 0 0 10px rgba(0, 0, 0, 0.2);
                    border: 5px solid #587D71;
                    position: relative;
                    overflow: visible;
                    filter: drop-shadow(0 0 10px rgba(0, 0, 0, 0.5));
                }

                .clock-face {
                    position: relative;
                    width: 100%;
                    height: 100%;
                    border-radius: 50%;
                }

                .hand {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform-origin: 50% 100%;
                }

                .hour {
                    width: 4px;
                    height: 35px;
                    background: #587D71;
                    border-radius: 3px;
                    margin-top: -35px;
                    margin-left: -2px;
                    z-index: 3;
                    box-shadow: 0 0 3px rgba(0,0,0,0.3);
                }

                .minute {
                    width: 3px;
                    height: 50px;
                    background: #7798AB;
                    border-radius: 2px;
                    margin-top: -50px;
                    margin-left: -1.5px;
                    z-index: 2;
                    box-shadow: 0 0 2px rgba(0,0,0,0.3);
                }

                .second {
                    width: 2px;
                    height: 60px;
                    background: #332c57;
                    margin-top: -60px;
                    margin-left: -1px;
                    z-index: 4;
                }

                .center-dot {
                    position: absolute;
                    width: 12px;
                    height: 12px;
                    background: #587D71;
                    border-radius: 50%;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    z-index: 5;
                    box-shadow: 0 0 3px rgba(0,0,0,0.5);
                    border: 2px solid #fff;
                }

                .center-dot::after {
                    content: '';
                    position: absolute;
                    width: 4px;
                    height: 4px;
                    background: #332c57;
                    border-radius: 50%;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                }

                .ticks {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                }

                .tick {
                    position: absolute;
                    left: 50%;
                    transform-origin: bottom center;
                }

                .tick-hour {
                    width: 2px;
                    height: 8px;
                    margin-left: -1px;
                    top: 5%;
                    background: #587D71;
                }

                .tick-minute {
                    width: 1px;
                    height: 4px;
                    margin-left: -0.5px;
                    top: 5%;
                    background: #7798AB;
                }

                .numbers {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    font-family: "Onesize";
                    font-weight: bold;
                    color: #587D71;
                }

                .number {
                    position: absolute;
                    font-size: 12px;
                    transform: translate(-50%, -50%);
                }

                .digital-time {
                    position: absolute;
                    bottom: -25px;
                    left: 0;
                    width: 100%;
                    text-align: center;
                    font-family: "Onesize";
                    font-size: 14px;
                    font-weight: bold;
                    color: #fff;
                    text-shadow: 0 0 3px #000;
                    background: #587D71;
                    padding: 3px;
                    border-radius: 3px;
                    border: 1px solid #7798AB;
                }

                .toggle-button {
                    position: absolute;
                    top: -20px;
                    right: 0;
                    background: #587D71;
                    color: #e74c3c;
                    border: none;
                    border-radius: 3px;
                    padding: 2px 6px;
                    font-size: 10px;
                    cursor: pointer;
                    z-index: 10;
                    border: 1px solid #7798AB;
                    font-family: "Onesize";
                    font-weight: bold;
                }

                .toggle-button:hover {
                    background: #7798AB;
                    color: #c0392b;
                }
            </style>
            <div class="clock-container">
                <button class="toggle-button">${this._isVisible ? "×" : "○"}</button>
                <div class="clock-face">
                    <div class="ticks">
                        ${this.renderTicks()}
                    </div>
                    <div class="numbers">
                        ${this.renderNumbers()}
                    </div>
                    <div class="hand hour"></div>
                    <div class="hand minute"></div>
                    <div class="hand second"></div>
                    <div class="center-dot"></div>
                    <div class="digital-time"></div>
                </div>
            </div>
        `;

        // Add event listener for the toggle button.
        const button: HTMLButtonElement = this._clockElement.querySelector(".toggle-button") as HTMLButtonElement;
        button.onclick = (e: MouseEvent) => {
            e.stopPropagation();
            this.toggleClockVisibility();
        };

        // Start or stop the clock based on visibility.
        if (this._isVisible) {
            this.startClock();
        }
        else {
            this.stopClock();
        }
    }

    // Renders the clock's ticks (minute and hour markers).
    private renderTicks(): string {
        let ticksHTML: string = "";
        for (let i: number = 0; i < 60; i++) {
            const angle: number = i * 6;
            const isHourTick: boolean = i % 5 === 0;
            const tickClass: string = isHourTick ? "tick-hour" : "tick-minute";
            const rotation: string = `rotate(${angle}deg)`;

            ticksHTML += `
                <div class="tick ${tickClass}" style="transform: ${rotation}"></div>`;
        }
        return ticksHTML;
    }

    // Renders the clock's numbers (1 to 12).
    private renderNumbers(): string {
        let numbersHTML: string = "";
        const radius: number = 55;
        const center: number = 75;

        for (let i: number = 1; i <= 12; i++) {
            const angle: number = (i * 30) - 90;
            const radian: number = angle * (Math.PI / 180);
            const x: number = center + radius * Math.cos(radian);
            const y: number = center + radius * Math.sin(radian);

            numbersHTML += `
                <div class="number" style="left: ${x}px; top: ${y}px;">${i}</div>`;
        }
        return numbersHTML;
    }

    // Makes the clock draggable by adding mouse event listeners.
    private makeDraggable(): void {
        const container: HTMLElement = this.shadowRoot?.querySelector(".clock-container") as HTMLElement;

        container.addEventListener("mousedown", (e: MouseEvent) => {
            if (e.target !== container && !container.contains(e.target as Node)) return;

            this._isDragging = true;
            const rect: DOMRect = container.getBoundingClientRect();
            this._dragOffset = {
                x: e.clientX - rect.left,
                y: e.clientY - rect.top,
            };

            container.style.transition = "none";
            document.body.style.cursor = "grabbing";
        });

        document.addEventListener("mousemove", (e: MouseEvent) => {
            if (!this._isDragging) return;

            const host: HTMLElement = this.shadowRoot?.host as HTMLElement;
            host.style.left = `${e.clientX - this._dragOffset.x}px`;
            host.style.top = `${e.clientY - this._dragOffset.y}px`;
        });

        document.addEventListener("mouseup", () => {
            if (this._isDragging) {
                this._isDragging = false;
                container.style.transition = "";
                document.body.style.cursor = "";
            }
        });
    }

    // Toggles the visibility of the clock.
    private toggleClockVisibility(): void {
        this._isVisible = !this._isVisible;
        this.render();
    }

    // Starts the clock by updating the hands and digital time.
    private startClock(): void {
        if (this._intervalId !== null) {
            clearInterval(this._intervalId);
        }

        const updateClock: () => void = (): void => {
            const now: Date = new Date();
            const hours: number = now.getHours();
            const minutes: number = now.getMinutes();
            const seconds: number = now.getSeconds();
            const milliseconds: number = now.getMilliseconds();

            const hourHand: HTMLDivElement = this.shadowRoot?.querySelector(".hour") as HTMLDivElement;
            const minuteHand: HTMLDivElement = this.shadowRoot?.querySelector(".minute") as HTMLDivElement;
            const secondHand: HTMLDivElement = this.shadowRoot?.querySelector(".second") as HTMLDivElement;
            const digitalTime: HTMLDivElement = this.shadowRoot?.querySelector(".digital-time") as HTMLDivElement;

            // Calculate the rotation angles for the clock hands.
            const secondDegrees: number = seconds * 6 + milliseconds * 0.006;
            const minuteDegrees: number = minutes * 6 + seconds * 0.1;
            const hourDegrees: number = (hours % 12) * 30 + minutes * 0.5;

            hourHand.style.transform = `rotate(${hourDegrees}deg)`;
            minuteHand.style.transform = `rotate(${minuteDegrees}deg)`;
            secondHand.style.transform = `rotate(${secondDegrees}deg)`;

            // Update the digital time display.
            digitalTime.textContent = now.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: !this._is24HourFormat,
            });
        };

        updateClock();
        this._intervalId = window.setInterval(updateClock, 50);
    }

    // Stops the clock by clearing the interval and resetting dragging state.
    private stopClock(): void {
        if (this._intervalId !== null) {
            clearInterval(this._intervalId);
            this._isDragging = false;
            this._intervalId = null;
        }
    }
}

// Define the custom element.
customElements.define("game-time", TimeComponent);
